/**
 * Manages the active workstation.
 *
 * When instantiated, looks up for a workstation id in local storage and, if found,
 * queries the API for a full workstation object.
 *
 * @type {ngService}
 */
export class ActiveWorkstation {
  static ngType = 'service'

  constructor(
    $rootScope,
    $state,
    TmrLocalStorage,
    Workstation,
    WorkstationAntennas,
    TmrNoActiveWorkstationError
  ) {
    'ngInject'
    Object.assign(this, {
      $rootScope,
      $state,
      TmrLocalStorage,
      Workstation,
      WorkstationAntennas,
      TmrNoActiveWorkstationError,
    })

    $rootScope.$on('logout', () => this.onLogout())
  }

  /**
   * Manages errors when loading the active workstation.
   * If the stored id doesn't match any workstation, clear and reload.
   */
  onGetActiveError(err) {
    if (err.status === 404) {
      this.TmrLocalStorage.clear('workstation')
      this.$state.reload()
    } else throw err
  }

  /**
   * Returns the active workstation.
   *
   * @returns {Workstation}
   */
  get() {
    if (this.activeWorkstation) return this.activeWorkstation

    // Request full workstation object from the API.
    let id = this.TmrLocalStorage.get('workstation')
    if (id) {
      this.activeWorkstation = this.Workstation.getActive({ id })
      this.activeWorkstation.$promise
        .catch(err => this.onGetActiveError(err))
        .then(workstation => this.emit(workstation, true))
    } else {
      this.activeWorkstation = this.Workstation.create()
      this.activeWorkstation.$promise = Promise.resolve(this.activeWorkstation)
    }
    return this.activeWorkstation
  }

  /**
   * Sets the active workstation.
   *
   * @param {Workstation} workstation
   * @returns {Promise}
   */
  set(workstation, redirect) {
    let oldId =
      this.activeWorkstation && this.activeWorkstation.id
        ? this.activeWorkstation.id
        : undefined

    if (this.activeWorkstation === undefined) {
      this.activeWorkstation = workstation
    } else {
      // Extend instead of setting to keep reference.
      Object.assign(this.activeWorkstation, workstation)
      // Resolve promise to avoid rethrowing old errors.
      this.activeWorkstation.$promise = Promise.resolve(this.activeWorkstation)
      // Reset workstation antennas.
      delete this.activeWorkstation.$workstationAntennas
    }

    if (oldId !== workstation.id) {
      this.TmrLocalStorage.set('workstation', workstation.id)
      // Go to 'redirect' or Reload state, then broadcast new active workstation.
      return (
        redirect
          ? this.$state.go(redirect, null, { reload: true, inherit: false })
          : this.$state.reload()
      ).then(() => this.emit(workstation))
    }

    return Promise.resolve()
  }

  /**
   * Broadcasts to app that the active workstation has just been changed
   *
   * @param {Workstation} workstation
   * @param {boolean} first If true, the workstation is being set for the first time
   */
  emit(workstation, first = false) {
    if (workstation)
      this.$rootScope.$emit('activeworkstationset', workstation, first)
  }

  /**
   * Retrieves a promise of the array of the active workstation's antennas.
   *
   * @returns {Promise<WorkstationAntennas>}
   */
  getAntennas() {
    return this.get()
      .$promise.then(workstation => {
        if (!workstation.id) throw new this.TmrNoActiveWorkstationError()
        this.activeWorkstation.$workstationAntennas =
          this.activeWorkstation.$workstationAntennas ??
          new this.WorkstationAntennas(workstation)
        return this.activeWorkstation.$workstationAntennas
      })
      .catch(err => this.onGetActiveError(err))
  }

  /**
   * Clears active workstation on logout.
   */
  onLogout() {
    delete this.activeWorkstation
  }
}
